import { createContext, useEffect, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import isNumber from "lodash/isNumber";
import PropTypes from "prop-types";

import { useAnalyticsTracker } from "components/instrumentation/source/analytics";
import { WWW_BUILDING_COLLECTIONS_AVAILABILITY_QUERY } from "components/pages/property/listing/graphql";
import {
  buildPropertyListingPath,
  updateSearchPath,
  useAppContext,
  useBootstrap,
  useSearchContext,
} from "components/store/source";
import { setCurrencySymbol } from "components/store/source/actions/searchCriteriaActions";
import { buildingListingTrackingEvents } from "components/store/source/tracking_events";

import { useListingUpgradeServiceQueryLazyQuery } from "types/graphql";

const initialPropertyListingState = {
  listing: null,
  isListingLoading: false,
  collections: null,
  isCollectionLoading: false,
  filters: null,
};

export const PropertyListingContext = createContext(
  initialPropertyListingState,
);

export function PropertyListingContextProvider({
  listing,
  isListingLoading,
  children,
}) {
  const { currency } = useAppContext();
  const { analytics } = useAnalyticsTracker();
  const {
    urlParams,
    featureFlags: { ancillariesAsUpgrades },
  } = useBootstrap();
  const { criteria, dispatch } = useSearchContext();
  const [collectionData, setData] = useState(null);
  const [getCollections, { called, data, loading }] = useLazyQuery(
    WWW_BUILDING_COLLECTIONS_AVAILABILITY_QUERY,
  );

  const [
    getAvailableUpgrades,
    { data: availableUpgradesData, loading: isAvailableUpgradesLoading },
  ] = useListingUpgradeServiceQueryLazyQuery();

  const getVariables = () => {
    const {
      checkIn,
      checkOut,
      guests: { guests: sleeps },
      priceMin,
      priceMax,
    } = criteria;

    const isPriceRangeProvided = isNumber(priceMin) && isNumber(priceMax);
    return {
      listingId: listing.id.toString(),
      ...(checkIn && { checkInDate: checkIn }),
      ...(checkOut && { checkOutDate: checkOut }),
      ...{ prices: isPriceRangeProvided ? [priceMin, priceMax] : [] },
      ...{ sleeps: sleeps || 1 },
      ...(urlParams?.utmSource && { source: urlParams.utmSource }),
      targetCurrency: currency,
    };
  };

  useEffect(() => {
    if (!dispatch || !listing || !listing.currency) return;

    // Get currency symbol from listing info and set to search context
    // https://stackoverflow.com/questions/19373860/convert-currency-names-to-currency-symbol
    dispatch(
      setCurrencySymbol(
        Number()
          .toLocaleString(undefined, {
            style: "currency",
            currency,
          })
          .replace(/[0-9.,]+/, ""),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, listing]);

  useEffect(() => {
    if (
      listing &&
      criteria &&
      Boolean(criteria.checkIn) === Boolean(criteria.checkOut)
    ) {
      // Update current url
      updateSearchPath(buildPropertyListingPath(criteria, urlParams));

      // Start a gql request
      getCollections({ variables: getVariables() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing, criteria, urlParams, getCollections, currency]);

  useEffect(() => {
    if (collectionData?.collections?.length && ancillariesAsUpgrades) {
      getAvailableUpgrades({
        variables: {
          collectionId: Number(collectionData.collections?.[0]?.collectionId),
          targetCurrency: currency,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionData, currency, getAvailableUpgrades, ancillariesAsUpgrades]);

  useEffect(() => {
    if (data) {
      setData(data?.buildingCollectionsAvailability);
      const availableCollections =
        data?.buildingCollectionsAvailability?.collections.filter(
          collection => collection.available,
        );
      const availableCollectionIdsWithUnitsLeftWarning =
        availableCollections.reduce((acc, memo) => {
          if (memo.quote?.remainingUnitsCount) {
            acc.push(memo.collectionId);
          }
          return acc;
        }, []);
      analytics.track(buildingListingTrackingEvents.quoteUpdated, {
        ...getVariables(),
        listingId: listing?.id,
        availableCollectionIds: availableCollections.map(
          collection => collection.collectionId,
        ),
        availableCollectionIdsWithUnitsLeftWarning,
        buildingListing: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <PropertyListingContext.Provider
      value={{
        ...initialPropertyListingState,
        availableUpgrades: availableUpgradesData?.upgradeService ?? [],
        isAvailableUpgradesLoading,
        listing,
        collections: collectionData?.collections,
        filters: collectionData?.filters,
        isCollectionLoading: !called || loading,
        isListingLoading,
      }}
    >
      {children}
    </PropertyListingContext.Provider>
  );
}

PropertyListingContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  listing: PropTypes.object,
  filters: PropTypes.object,
  isListingLoading: PropTypes.bool.isRequired,
  urlParams: PropTypes.object,
};

export const withPropertyListingContext =
  WrappedComponent =>
  // eslint-disable-next-line react/display-name
  ({ listing, filters, isListingLoading, urlParams, others }) => {
    return (
      <PropertyListingContextProvider
        listing={listing}
        filters={filters}
        isListingLoading={isListingLoading}
        urlParams={urlParams}
      >
        <WrappedComponent {...others} />
      </PropertyListingContextProvider>
    );
  };
